
import React, { useState, useRef, useEffect } from 'react';
import { X, UploadCloud, FileText, CheckCircle, Image as ImageIcon, Loader2, RefreshCw, Trash2, Mail, Link as LinkIcon, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { uploadPDF, updateBooklet, deleteBooklet } from '../services/firebase';
import { pdfjs } from 'react-pdf';
import { Booklet, User } from '../types';
import { AppTutorial } from './AppTutorial';

// Ensure worker is configured for cover generation
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: (booklet: Booklet) => void;
  onDeleteComplete?: (id: string) => void;
  initialBooklet?: Booklet | null;
  currentUser?: User | null;
  runTutorial?: boolean;
}

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose, onUploadComplete, onDeleteComplete, initialBooklet, currentUser, runTutorial = false }) => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  
  // Status & Visibility
  const [statusMode, setStatusMode] = useState<'published' | 'scheduled' | 'draft'>('published');
  const [visibility, setVisibility] = useState<'public' | 'unlisted'>('public');
  const [scheduleDate, setScheduleDate] = useState<string>('');
  
  // Lead Gen
  const [emailGate, setEmailGate] = useState(false);
  
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  
  // Cover generation state
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [generatingCover, setGeneratingCover] = useState(false);
  
  // Drag and Drop State
  const [isDragging, setIsDragging] = useState(false);

  const [showTutorial, setShowTutorial] = useState(runTutorial);
  useEffect(() => {
    if (runTutorial) setShowTutorial(true);
  }, [runTutorial]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null); 
  const isEditMode = !!initialBooklet;

  // Pre-fill data when opening in edit mode
  useEffect(() => {
    if (isOpen) {
        setStatus('idle');
        setProgress(0);

        if (initialBooklet) {
            setTitle(initialBooklet.title);
            setDescription(initialBooklet.description);
            setCoverPreview(initialBooklet.coverUrl || null);
            setStatusMode(initialBooklet.status || 'published');
            setVisibility(initialBooklet.visibility || 'public');
            setEmailGate(initialBooklet.emailGate || false);
            
            if (initialBooklet.scheduledAt) {
                const date = new Date(initialBooklet.scheduledAt);
                const offset = date.getTimezoneOffset() * 60000;
                const localIsoString = new Date(date.getTime() - offset).toISOString().slice(0, 16);
                setScheduleDate(localIsoString);
                setStatusMode('scheduled');
            } else {
                setScheduleDate('');
            }

            setFile(null); 
        } else {
            resetForm();
        }
    }
  }, [isOpen, initialBooklet]);

  const generateCover = async (pdfFile: File) => {
    setGeneratingCover(true);
    try {
        const url = URL.createObjectURL(pdfFile);
        const loadingTask = pdfjs.getDocument(url);
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        if (context) {
            await page.render({ canvasContext: context, viewport } as any).promise;
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
            setCoverPreview(dataUrl);
        }
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error("Error generating cover:", err);
    } finally {
        setGeneratingCover(false);
    }
  };

  const processFile = (selectedFile: File) => {
      const MAX_SIZE_MB = 100;
      const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

      if (selectedFile.size > MAX_SIZE_BYTES) {
        alert(`File is too large. Please upload a PDF smaller than ${MAX_SIZE_MB}MB.`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      if (selectedFile.type === 'application/pdf') {
        setFile(selectedFile);
        if (!title.trim() && !isEditMode) {
            setTitle(selectedFile.name.replace('.pdf', ''));
        }
        generateCover(selectedFile);
      } else {
        alert("Please select a valid PDF file.");
      }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
        const imageFile = e.target.files[0];
        const reader = new FileReader();
        reader.onloadend = () => {
            setCoverPreview(reader.result as string);
        };
        reader.readAsDataURL(imageFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragEnter = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
      e.preventDefault(); e.stopPropagation(); setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
          processFile(e.dataTransfer.files[0]);
      }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this publication?")) {
        setStatus('uploading');
        try {
            if (initialBooklet && onDeleteComplete) {
                await deleteBooklet(initialBooklet.id);
                onDeleteComplete(initialBooklet.id);
                onClose();
            }
        } catch (err) {
            console.error(err);
            setStatus('error');
        }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditMode && !file) return;

    setStatus('uploading');
    setProgress(0);

    const scheduledTimestamp = statusMode === 'scheduled' && scheduleDate 
        ? new Date(scheduleDate).getTime() 
        : null;
        
    const finalStatus = statusMode;

    try {
      let resultBooklet: Booklet;

      const metadata = { 
        title, 
        description,
        status: finalStatus,
        visibility,
        emailGate,
        scheduledAt: scheduledTimestamp,
        ownerId: currentUser?.uid || 'anon',
        ownerName: currentUser?.displayName || 'Anonymous'
      };

      if (isEditMode && initialBooklet) {
        const updateMeta = {
            ...metadata,
            ownerId: initialBooklet.ownerId || metadata.ownerId, 
            ownerName: initialBooklet.ownerName || metadata.ownerName
        };

        resultBooklet = await updateBooklet(
            initialBooklet.id,
            file,
            updateMeta,
            (p) => setProgress(p),
            coverPreview
        );
      } else {
        resultBooklet = await uploadPDF(
            file!, 
            metadata, 
            (p) => setProgress(p),
            coverPreview 
        );
      }
      
      setStatus('success');
      setTimeout(() => {
        onUploadComplete(resultBooklet);
        if (!isEditMode) resetForm();
      }, 1000);
      
    } catch (err: any) {
      console.error(err);
      alert("Upload failed. Please check your connection and try again.");
      setStatus('error');
    }
  };

  const resetForm = () => {
    setFile(null);
    setTitle('');
    setDescription('');
    setCoverPreview(null);
    setProgress(0);
    setStatus('idle');
    setStatusMode('published');
    setVisibility('public');
    setEmailGate(false);
    setScheduleDate('');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <AppTutorial mode="upload" run={showTutorial} onFinish={() => setShowTutorial(false)} />
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-dark/60 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-3xl bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col max-h-[95vh]"
          >
            {/* Header */}
            <div className="px-4 md:px-8 py-4 md:py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0">
              <h2 className="font-serif text-xl md:text-2xl text-dark truncate mr-4">
                {isEditMode ? 'Edit Publication' : 'Upload Publication'}
              </h2>
              <button onClick={onClose} className="text-cool hover:text-dark transition-colors">
                <X size={24} />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 md:p-8 overflow-y-auto">
              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <motion.div 
                    initial={{ scale: 0 }} 
                    animate={{ scale: 1 }}
                    className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-4"
                  >
                    <CheckCircle size={32} />
                  </motion.div>
                  <h3 className="font-serif text-2xl text-dark mb-2">
                      {isEditMode ? 'Updates Saved' : 'Upload Complete'}
                  </h3>
                  <p className="text-cool">
                    {visibility === 'unlisted' ? 'Saved as Unlisted. Link required to view.' : 'Your booklet is live.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  
                  {isEditMode && (
                     <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3 text-blue-900 mb-4 text-xs md:text-sm">
                        <RefreshCw className="w-5 h-5 shrink-0 text-blue-600 mt-0.5" />
                        <div>
                            <p className="font-bold mb-1">Editing Mode</p>
                            <p>You are updating <strong>{initialBooklet?.title}</strong>. Metadata changes are instant.</p>
                        </div>
                     </div>
                  )}

                  <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                      {/* Left: File & Cover */}
                      <div className="w-full md:w-1/3 flex-shrink-0 tour-upload-dropzone">
                          <div 
                            className={`border-2 border-dashed rounded-xl h-48 md:h-64 flex flex-col items-center justify-center text-center transition-all relative overflow-hidden mb-2 
                                ${isDragging ? 'border-dark bg-gray-100 scale-[1.02] ring-2 ring-dark/20' : ''} 
                                ${file && !isDragging ? 'border-warm bg-gray-50' : ''} 
                                ${!file && !isDragging ? 'border-gray-200 hover:border-warm/50 hover:bg-gray-50' : ''}
                            `}
                            onClick={() => !coverPreview && fileInputRef.current?.click()}
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          >
                            <input 
                              type="file" 
                              ref={fileInputRef} 
                              className="hidden" 
                              accept="application/pdf" 
                              onChange={handleFileChange}
                            />
                            <input 
                              type="file" 
                              ref={coverInputRef} 
                              className="hidden" 
                              accept="image/*" 
                              onChange={handleCoverChange}
                            />
                            
                            {generatingCover ? (
                                <div className="flex flex-col items-center text-cool">
                                    <Loader2 className="animate-spin mb-2" size={32} />
                                    <span className="text-xs font-semibold uppercase tracking-wider">Generating Preview...</span>
                                </div>
                            ) : coverPreview ? (
                                <div className="absolute inset-0 w-full h-full group">
                                    <img src={coverPreview} alt="Cover Preview" className="w-full h-full object-contain p-4 bg-gray-50" />
                                    <div className="absolute inset-0 bg-dark/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-4">
                                        <button 
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); coverInputRef.current?.click(); }}
                                            className="px-4 py-2 bg-white text-dark rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-gray-100 transition-colors w-full justify-center"
                                        >
                                            <ImageIcon size={14} /> Change Cover
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                            className="px-4 py-2 bg-transparent border border-white/30 text-white rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-white/10 transition-colors w-full justify-center"
                                        >
                                            <UploadCloud size={14} /> Replace PDF
                                        </button>
                                        <p className="text-[9px] text-white/80 mt-2 text-center font-medium leading-tight">
                                            Grid displays at 3:4 ratio.<br/>Preview shows full image.
                                        </p>
                                    </div>
                                </div>
                            ) : file ? (
                              <div className="flex flex-col items-center gap-2 text-dark p-4">
                                <FileText className="text-warm" size={32} />
                                <span className="font-medium text-sm line-clamp-2">{file.name}</span>
                              </div>
                            ) : (
                              <div className="space-y-2 p-4 pointer-events-none">
                                <UploadCloud className={`mx-auto transition-colors ${isDragging ? 'text-dark' : 'text-warm'}`} size={36} strokeWidth={1} />
                                <p className="text-sm font-medium text-dark">
                                    {isDragging ? 'Drop PDF Here' : (isEditMode ? 'Upload New PDF' : 'Drag PDF Here')}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <div className="text-center mt-2">
                            <p className="text-xs text-cool">
                                {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Max 100MB'}
                            </p>
                            <p className="text-[10px] text-cool/60 mt-1 font-medium">
                                Recommended Cover: 3:4 Ratio (e.g. 600x800)
                            </p>
                          </div>
                      </div>

                      {/* Right: Metadata & Settings */}
                      <div className="flex-1 space-y-5 tour-upload-meta">
                        <div>
                          <label className="block text-xs font-semibold text-cool uppercase tracking-wide mb-1.5">Title</label>
                          <input 
                            type="text" 
                            required
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-4 py-2.5 rounded-md border border-gray-200 focus:border-dark focus:ring-1 focus:ring-dark outline-none transition-all font-serif text-base md:text-lg placeholder-gray-300"
                            placeholder="e.g., Spring Catalog 2024"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-semibold text-cool uppercase tracking-wide mb-1.5">Description</label>
                          <textarea 
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full px-4 py-2.5 rounded-md border border-gray-200 focus:border-dark focus:ring-1 focus:ring-dark outline-none transition-all font-sans text-sm h-24 resize-none placeholder-gray-300"
                            placeholder="Brief summary..."
                          />
                        </div>

                        {/* Visibility & Status */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 tour-upload-status">
                                <label className="block text-xs font-semibold text-cool uppercase tracking-wide mb-3">Status</label>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input type="radio" checked={statusMode === 'published'} onChange={() => setStatusMode('published')} className="text-dark" />
                                        <span>Publish Now</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input type="radio" checked={statusMode === 'draft'} onChange={() => setStatusMode('draft')} className="text-dark" />
                                        <span>Draft</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input type="radio" checked={statusMode === 'scheduled'} onChange={() => setStatusMode('scheduled')} className="text-dark" />
                                        <span>Schedule</span>
                                    </label>
                                </div>
                                {statusMode === 'scheduled' && (
                                    <input 
                                        type="datetime-local" 
                                        value={scheduleDate}
                                        onChange={(e) => setScheduleDate(e.target.value)}
                                        className="mt-2 w-full px-2 py-1 border rounded text-xs"
                                    />
                                )}
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 tour-upload-visibility">
                                <label className="block text-xs font-semibold text-cool uppercase tracking-wide mb-3">Visibility</label>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 cursor-pointer text-sm group" title="Visible on public dashboard">
                                        <input type="radio" checked={visibility === 'public'} onChange={() => setVisibility('public')} className="text-dark" />
                                        <Globe size={14} className="text-cool" /> <span>Public</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer text-sm group" title="Only accessible via link">
                                        <input type="radio" checked={visibility === 'unlisted'} onChange={() => setVisibility('unlisted')} className="text-dark" />
                                        <LinkIcon size={14} className="text-cool" /> <span>Unlisted</span>
                                    </label>
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-200 tour-upload-gate">
                                    <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
                                        <input type="checkbox" checked={emailGate} onChange={(e) => setEmailGate(e.target.checked)} className="rounded text-dark focus:ring-dark" />
                                        <Mail size={14} /> Require Email
                                    </label>
                                    <p className="text-[10px] text-cool mt-1 leading-tight">Viewers must enter email to see document.</p>
                                </div>
                            </div>
                        </div>

                      </div>
                  </div>

                  {status === 'uploading' && (
                    <div className="space-y-2">
                        <div className="flex justify-between text-xs font-semibold uppercase text-cool">
                            <span>Processing...</span>
                            <span>{Math.round(progress)}%</span>
                        </div>
                        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                            <motion.div 
                                className="h-full bg-warm"
                                initial={{ width: 0 }}
                                animate={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>
                  )}

                  <div className="flex flex-col-reverse md:flex-row justify-between pt-2 items-center gap-4">
                    <div className="w-full md:w-auto">
                        {isEditMode && (
                            <button 
                                type="button" 
                                onClick={handleDelete}
                                className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-3 rounded-full text-red-600 hover:bg-red-50 text-sm font-semibold transition-colors"
                            >
                                <Trash2 size={16} /> <span className="md:hidden">Delete</span> <span className="hidden md:inline">Delete Publication</span>
                            </button>
                        )}
                    </div>
                    <div className="flex gap-3 w-full md:w-auto">
                        <button 
                        type="button" 
                        onClick={onClose}
                        className="flex-1 md:flex-none px-6 py-3 rounded-full text-cool hover:bg-gray-100 font-semibold transition-colors text-sm md:text-base"
                        disabled={status === 'uploading'}
                        >
                        Cancel
                        </button>
                        <button 
                        type="submit" 
                        disabled={(!file && !isEditMode) || status === 'uploading' || (statusMode === 'scheduled' && !scheduleDate)}
                        className={`flex-1 md:flex-none px-8 py-3 rounded-full font-semibold transition-all text-sm md:text-base ${
                            (!file && !isEditMode) || status === 'uploading' || (statusMode === 'scheduled' && !scheduleDate)
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                            : 'bg-dark text-white hover:bg-cool shadow-lg hover:shadow-xl'
                        }`}
                        >
                        {status === 'uploading' ? 'Processing...' : 'Save'}
                        </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default UploadModal;
